/**
 * Share Book Page (Digital aktiebok)
 *
 * Main page for managing a company's digital share book according to ABL 5 kap.
 * Shows shareholders, share classes, transactions, and audit trail.
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  BookOpen,
  Users,
  Layers,
  ArrowRightLeft,
  Shield,
  AlertTriangle,
  Download,
  Settings,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { EditShareBookModal } from "./components/EditShareBookModal";
import { companiesApi, shareBookApi } from "@/lib/api";
import { useShareBook, useShareholdersSummary, useRecentTransactions, shareBookKeys } from "@/hooks/use-share-book";
import { ShareBookSetup } from "./components/ShareBookSetup";
import { ShareholderTable } from "./components/ShareholderTable";
import { ShareClassCard } from "./components/ShareClassCard";
import { TransactionHistory } from "./components/TransactionHistory";
import { ShareBookEventLog } from "./components/ShareBookEventLog";
import { PaymentModal } from "@/components/PaymentModal";
import { Spinner } from "@/components/ui/spinner";
import { formatCurrency } from "@/lib/format";

interface Tab {
  id: string;
  label: string;
  icon: React.ElementType;
}

const TABS: Tab[] = [
  { id: "overview", label: "Översikt", icon: BookOpen },
  { id: "shareholders", label: "Aktieägare", icon: Users },
  { id: "share-classes", label: "Aktieslag", icon: Layers },
  { id: "transactions", label: "Transaktioner", icon: ArrowRightLeft },
  { id: "events", label: "Händelselogg", icon: Shield },
];

export function ShareBookPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const [activeTab, setActiveTab] = useState("overview");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [hasUserDismissedModal, setHasUserDismissedModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showEditShareBookModal, setShowEditShareBookModal] = useState(false);
  // Transaction filter by shareholder
  const [filterShareholderId, setFilterShareholderId] = useState<string | null>(null);
  const [filterShareholderName, setFilterShareholderName] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch company info (for the setup wizard when no share book exists)
  const { data: companyData, isLoading: isLoadingCompany } = useQuery({
    queryKey: ["company", companyId],
    queryFn: async () => {
      if (!companyId) return null;
      const response = await companiesApi.get(companyId);
      if (response.success && response.data) {
        return response.data.company;
      }
      return null;
    },
    enabled: !!companyId,
  });

  // Fetch share book data
  const { data: shareBook, isLoading: isLoadingShareBook, error: shareBookError } = useShareBook(companyId);

  // Only fetch shareholders and transactions when share book exists (no error)
  const shareBookExists = !!shareBook && !shareBookError;
  const { data: shareholders = [] } = useShareholdersSummary(companyId, { enabled: shareBookExists });
  const { data: recentTransactions = [] } = useRecentTransactions(companyId, 5, { enabled: shareBookExists });

  // Check user permissions
  const userRole = companyData?.role;
  const canEdit = userRole === "owner" || userRole === "manager" || userRole === "editor";

  // Handle viewing shareholder transaction history
  const handleViewShareholderHistory = (shareholderId: string, shareholderName: string) => {
    setFilterShareholderId(shareholderId);
    setFilterShareholderName(shareholderName);
    setActiveTab("transactions");
  };

  // Clear shareholder filter
  const handleClearShareholderFilter = () => {
    setFilterShareholderId(null);
    setFilterShareholderName(null);
  };

  // Callback when share book setup is complete
  const handleSetupComplete = () => {
    // Invalidate and refetch share book data
    queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId!) });
  };

  // Handle PDF export
  const handleExportPdf = async () => {
    if (!companyId) return;

    setIsExporting(true);
    try {
      const blob = await shareBookApi.exportPdf(companyId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `aktiebok-${companyData?.organizationNumber || companyId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Aktieboken har exporterats");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Kunde inte exportera PDF");
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate subscription error state (before conditional returns for hooks)
  const errorMessage = shareBookError instanceof Error ? shareBookError.message : "";
  const isSubscriptionError = errorMessage.includes("aktiverat") || errorMessage.includes("prenumeration");

  // Auto-show payment modal when subscription error occurs (only once)
  useEffect(() => {
    if (isSubscriptionError && !showPaymentModal && !hasUserDismissedModal) {
      setShowPaymentModal(true);
    }
  }, [isSubscriptionError, showPaymentModal, hasUserDismissedModal]);

  // Handle loading state
  if (isLoadingShareBook || isLoadingCompany) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  // Handle non-subscription errors
  if (shareBookError && !isSubscriptionError) {
    return (
      <div className="space-y-6">
        <Link
          to={`/dashboard/companies/${companyId}`}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka till företaget
        </Link>

        <div className="card">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="text-red-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Ett fel uppstod
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {errorMessage || "Kunde inte ladda aktieboken"}
            </p>
            <Link
              to={`/dashboard/companies/${companyId}`}
              className="btn btn-primary"
            >
              Gå till företagssidan
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Handle subscription error - show empty state with payment modal
  if (isSubscriptionError) {
    return (
      <div className="space-y-6">
        <Link
          to={`/dashboard/companies/${companyId}`}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka till företaget
        </Link>

        <div className="card">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="text-primary-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Digital aktiebok
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Hantera din aktiebok digitalt enligt ABL 5 kap. Registrera aktieägare,
              aktieslag och spåra alla transaktioner.
            </p>
            <button
              onClick={() => {
                setHasUserDismissedModal(false);
                setShowPaymentModal(true);
              }}
              className="btn btn-primary"
            >
              Kom igång
            </button>
          </div>
        </div>

        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setHasUserDismissedModal(true);
          }}
          companyId={companyId!}
          companyName={companyData?.name || "Företaget"}
          onActivated={() => {
            setShowPaymentModal(false);
            // Refetch share book data after activation
            queryClient.invalidateQueries({ queryKey: shareBookKeys.book(companyId!) });
          }}
        />
      </div>
    );
  }

  // If no share book exists, show setup wizard
  if (!shareBook) {
    return (
      <div className="space-y-6">
        <Link
          to={`/dashboard/companies/${companyId}`}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-2" />
          Tillbaka till företaget
        </Link>
        <ShareBookSetup
          companyId={companyId!}
          companyName={companyData?.name || "Företag"}
          onComplete={handleSetupComplete}
        />
      </div>
    );
  }

  // Calculate summary statistics
  const totalShares = shareBook.totalShares;
  const totalVotes = shareBook.totalVotes;
  const totalShareholders = shareBook.totalShareholders;
  const shareCapital = parseFloat(shareBook.shareCapital);

  return (
    <div className="space-y-6">
      {/* ═══════════════════════════════════════════════════════════════════
          PREMIUM HEADER SECTION
      ═══════════════════════════════════════════════════════════════════ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 sm:p-8 shadow-2xl">
        {/* Decorative elements */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />

        {/* Back link - integrated into hero */}
        <Link
          to={`/dashboard/companies/${companyId}`}
          className="relative group inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors mb-5"
        >
          <ArrowLeft size={12} className="group-hover:-translate-x-0.5 transition-transform" />
          <span>Tillbaka till företaget</span>
        </Link>

        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/10 shadow-lg">
              <BookOpen className="text-white" size={32} />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
                <Sparkles className="text-amber-400" size={12} />
                <span className="text-white/80 text-xs font-medium tracking-wide">{shareBook.organizationNumber}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Aktiebok</h1>
              <p className="text-slate-300 text-sm mt-1">{shareBook.companyName}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {shareBook.isLocked && (
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 backdrop-blur-sm text-amber-200 rounded-xl border border-amber-500/20">
                <AlertTriangle size={16} />
                <span className="text-sm font-medium">Låst</span>
              </div>
            )}

            {canEdit && (
              <button
                onClick={() => setShowEditShareBookModal(true)}
                className="group relative overflow-hidden rounded-xl bg-white/10 backdrop-blur-sm px-4 py-2.5 border border-white/10 hover:bg-white/20 transition-all duration-300"
              >
                <div className="relative flex items-center gap-2 text-white">
                  <Settings size={16} />
                  <span className="font-medium text-sm">Inställningar</span>
                </div>
              </button>
            )}

            <div className={`relative group ${isExporting ? 'opacity-50' : ''}`}>
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
              <button
                onClick={handleExportPdf}
                disabled={isExporting}
                className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 px-4 py-2.5 shadow-lg transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                <div className="relative flex items-center gap-2 text-white font-medium text-sm">
                  {isExporting ? (
                    <>
                      <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      Exporterar...
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Exportera PDF
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Aktiekapital</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(shareCapital)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Totalt antal aktier</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalShares.toLocaleString("sv-SE")}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Totalt antal röster</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalVotes.toLocaleString("sv-SE")}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Aktieägare</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalShareholders}</p>
        </div>
      </div>

      {/* Clause indicators */}
      <div className="flex flex-wrap gap-2">
        {shareBook.hasPreemptionClause && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200/50">
            Förköpsförbehåll
          </span>
        )}
        {shareBook.hasConsentClause && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200/50">
            Samtyckesförbehåll
          </span>
        )}
        {shareBook.hasRedemptionClause && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200/50">
            Hembudsförbehåll
          </span>
        )}
        {shareBook.hasConversionClause && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-medium bg-green-50 text-green-700 border border-green-200/50">
            Omvandlingsförbehåll
          </span>
        )}
        {!shareBook.hasPreemptionClause &&
          !shareBook.hasConsentClause &&
          !shareBook.hasRedemptionClause &&
          !shareBook.hasConversionClause && (
            <span className="text-sm text-gray-400">Inga förbehåll registrerade</span>
          )}
      </div>

      {/* Tab bar - pill style */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
        <nav
          className="flex gap-1 p-1.5 overflow-x-auto scrollbar-hide"
          aria-label="Tabs"
        >
          {TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all whitespace-nowrap flex-shrink-0 ${
                  isActive
                    ? "bg-primary-50 text-primary-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "overview" && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent shareholders */}
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <Users size={16} className="text-white" />
                </div>
                <h3 className="font-bold text-gray-900">Största aktieägare</h3>
              </div>
              <button
                onClick={() => setActiveTab("shareholders")}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Visa alla
              </button>
            </div>
            {shareholders.length > 0 ? (
              <div className="space-y-3">
                {shareholders.slice(0, 5).map((shareholder) => (
                  <div
                    key={shareholder.id}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{shareholder.name}</p>
                      <p className="text-sm text-gray-500">
                        {shareholder.totalShares.toLocaleString("sv-SE")} aktier
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {shareholder.ownershipPercent.toFixed(1)}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {shareholder.votingPercent.toFixed(1)}% röster
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                Inga aktieägare registrerade
              </p>
            )}
          </div>

          {/* Recent transactions */}
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
                  <ArrowRightLeft size={16} className="text-white" />
                </div>
                <h3 className="font-bold text-gray-900">Senaste transaktioner</h3>
              </div>
              <button
                onClick={() => setActiveTab("transactions")}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Visa alla
              </button>
            </div>
            {recentTransactions.length > 0 ? (
              <div className="space-y-3">
                {recentTransactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {getTransactionTypeLabel(transaction.type)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(transaction.transactionDate).toLocaleDateString("sv-SE")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {transaction.quantity.toLocaleString("sv-SE")} aktier
                      </p>
                      {transaction.isVerified && (
                        <span className="text-xs text-green-600">Verifierad</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                Inga transaktioner registrerade
              </p>
            )}
          </div>

          {/* Share classes summary */}
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-6 md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-violet-400 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-violet-500/25">
                  <Layers size={16} className="text-white" />
                </div>
                <h3 className="font-bold text-gray-900">Aktieslag</h3>
              </div>
              <button
                onClick={() => setActiveTab("share-classes")}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Hantera
              </button>
            </div>
            {shareBook.shareClasses.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {shareBook.shareClasses.map((shareClass) => (
                  <div
                    key={shareClass.id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{shareClass.name}</span>
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                        {shareClass.code}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Antal aktier:</span>
                        <span className="text-gray-900">{shareClass.totalShares.toLocaleString("sv-SE")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Röstvärde:</span>
                        <span className="text-gray-900">{parseFloat(shareClass.votingPower)}</span>
                      </div>
                      {shareClass.isPreference && (
                        <span className="inline-block mt-2 text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                          Preferensaktie
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                Inga aktieslag skapade
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === "shareholders" && (
        <ShareholderTable
          companyId={companyId!}
          canEdit={canEdit}
          onViewHistory={handleViewShareholderHistory}
        />
      )}

      {activeTab === "share-classes" && (
        <ShareClassCard companyId={companyId!} shareClasses={shareBook.shareClasses} canEdit={canEdit} />
      )}

      {activeTab === "transactions" && (
        <TransactionHistory
          companyId={companyId!}
          shareBook={{
            hasPreemptionClause: shareBook.hasPreemptionClause,
            hasConsentClause: shareBook.hasConsentClause,
            hasRedemptionClause: shareBook.hasRedemptionClause,
            hasConversionClause: shareBook.hasConversionClause,
          }}
          canEdit={canEdit}
          filterShareholderId={filterShareholderId}
          filterShareholderName={filterShareholderName}
          onClearShareholderFilter={handleClearShareholderFilter}
        />
      )}

      {activeTab === "events" && (
        <ShareBookEventLog companyId={companyId!} />
      )}

      {/* Edit Share Book Modal */}
      {showEditShareBookModal && (
        <EditShareBookModal
          isOpen={showEditShareBookModal}
          onClose={() => setShowEditShareBookModal(false)}
          companyId={companyId!}
          shareBook={{
            shareCapital: shareBook.shareCapital,
            hasPreemptionClause: shareBook.hasPreemptionClause,
            hasConsentClause: shareBook.hasConsentClause,
            hasRedemptionClause: shareBook.hasRedemptionClause,
            hasConversionClause: shareBook.hasConversionClause,
          }}
        />
      )}
    </div>
  );
}

// Helper function for transaction type labels
function getTransactionTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    founding: "Bildande",
    new_issue: "Nyemission",
    bonus_issue: "Fondemission",
    split: "Split",
    reverse_split: "Omvänd split",
    reduction: "Minskning",
    purchase: "Köp",
    gift: "Gåva",
    inheritance: "Arv",
    estate_division: "Arvskifte",
    conversion: "Omvandling",
    redemption: "Inlösen",
    merger: "Fusion",
    demerger: "Fission",
    pledge: "Pantsättning",
    unpledge: "Hävd pantsättning",
    correction: "Rättelse",
  };
  return labels[type] || type;
}

export default ShareBookPage;
